import { MessagePageEvent } from "@/User/Constants/twilio";
import { TYPE_APPLY, TYPE_SCOUT } from "@/User/Constants/user-scout";
import { useTwilioClientContext } from "@/User/Contexts/TwilioClientContext";
import { getLastMessageFromTwilioConversation, handleLastMessageEventMessagePage, handleLastReadIndexEventMessagePage, handlleNewConversationMessagePage } from "@/User/Services/TwilioClientService";
import { usePage } from "@inertiajs/react";
import React, { createContext, useContext, useEffect } from "react";
import { useMessage } from "../Hooks/use-message";
import { useSendMessage } from "../Hooks/use-send-message";
import { useTabBar } from "../Hooks/use-tabbar";
import { useTwilioChat } from "../Hooks/use-twilio-chat";

const MessagePageContext = createContext();

const MessagePageProvider = ({ children }) => {
    const { conversation: dbConversation, auth: { user } } = usePage().props;
    const { twilioClient, getUnreadConversationCount } = useTwilioClientContext();
    const {
        tabList, selectedTab, setSelectedTab,
        allInited, scoutInited, applyInited, handleInit
    } = useTabBar();
    const {
        newConversation, allConversation, scoutConversation, applyConversation,
        setNewConversation, setAllConversation, setScoutConversation, setApplyConversation,
        callLoadMessageList, loadingMoreMessages
    } = useMessage();
    const { initialChat, conversation, messages, paginator, hasMore, fetchMore, loadingMore, setConversation } = useTwilioChat();
    const { sending, messageBody, setMessageBody, handleSendMessage } = useSendMessage({ conversation });


    /***** Event LastMessage ******/
    useEffect(() => {
        if (!twilioClient) {
            return;
        }

        const handleLastMessage = async (e) => {
            const eventConversation = e.detail.conversation;

            const lastMessage = await getLastMessageFromTwilioConversation({ conversation: eventConversation });

            const isMe = lastMessage?.author === user.data.uid;

            const isCurrent = dbConversation?.conversation_id === eventConversation.sid || isMe;

            // Call update tab bar
            !isCurrent && getUnreadConversationCount();

            // Check case app send message and not update lastReadIndex
            if (isMe && eventConversation.lastReadMessageIndex < lastMessage.index) {
                await eventConversation.setAllMessagesRead();
            }

            const { newAllConversations, newScoutConversations, newApplyConversations } = await handleLastMessageEventMessagePage({
                allConversation, scoutConversation, applyConversation, eventConversation, isCurrent
            });

            setAllConversation({ ...allConversation, items: [...newAllConversations] });
            setScoutConversation({ ...scoutConversation, items: [...newScoutConversations] });
            setApplyConversation({ ...applyConversation, items: [...newApplyConversations] });
        };

        const handleLastReadIndex = (e) => {
            const eventConversation = e.detail.conversation;

            // Call update tab bar
            getUnreadConversationCount();

            const { newAllConversations, newScoutConversations, newApplyConversations } = handleLastReadIndexEventMessagePage({
                allConversation, scoutConversation, applyConversation, eventConversation
            });

            setAllConversation({ ...allConversation, items: [...newAllConversations] });
            setScoutConversation({ ...scoutConversation, items: [...newScoutConversations] });
            setApplyConversation({ ...applyConversation, items: [...newApplyConversations] });
        };

        const handleAddNewConversation = async (e) => {
            const eventConversation = e.detail.conversation;

            const newConver = await handlleNewConversationMessagePage({
                allConversation,
                applyConversation,
                scoutConversation,
                eventConversation,
                userUid: user?.data?.uid,
                scoutInited,
                applyInited,
            });

            if (!newConver) {
                return;
            }

            setAllConversation({
                ...allConversation,
                items: [newConver, ...allConversation.items],
            });

            if (newConver.type === TYPE_APPLY && applyInited) {
                setApplyConversation({
                    ...applyConversation,
                    items: [newConver, ...applyConversation.items],
                });
            }

            if (newConver.type === TYPE_SCOUT && scoutInited) {
                setScoutConversation({
                    ...scoutConversation,
                    items: [newConver, ...scoutConversation.items],
                });
            }

            setNewConversation({
                all: [
                    ...newConversation.all,
                    newConver.id,
                ],
                scout: scoutInited ? [
                    ...newConversation.scout,
                    newConver.id
                ] : newConversation.scout,
                apply: applyInited ? [
                    ...newConversation.apply,
                    newConver.id
                ] : newConversation.apply,
            });
        };

        window.addEventListener(MessagePageEvent.lastMessage, handleLastMessage);
        window.addEventListener(MessagePageEvent.lastReadMessageIndex, handleLastReadIndex);
        window.addEventListener(MessagePageEvent.conversationJoined, handleAddNewConversation);

        return () => {
            window.removeEventListener(MessagePageEvent.lastMessage, handleLastMessage);
            window.removeEventListener(MessagePageEvent.lastReadMessageIndex, handleLastReadIndex);
            window.removeEventListener(MessagePageEvent.conversationJoined, handleAddNewConversation);
        };
    }, [twilioClient, allConversation, applyConversation, scoutConversation, dbConversation, user]);


    return (
        <MessagePageContext.Provider value={{
            // Handle TabBar
            tabList, selectedTab, setSelectedTab,
            allInited, scoutInited, applyInited, handleInit,
            // Handle conversation list
            allConversation, scoutConversation, applyConversation, callLoadMessageList, loadingMoreMessages,
            // Handle Chat list
            initialChat, conversation, messages, paginator, hasMore, fetchMore, loadingMore, setConversation,
            sending, messageBody, setMessageBody, handleSendMessage,
        }}>
            {children}
        </MessagePageContext.Provider>
    );
};

const useMessagePageContext = () => {
    const context = useContext(MessagePageContext);

    if (!context) {
        throw new Error("useMessagePageContext must be used within MessagePageProvider");
    }

    return context;
};

export { MessagePageProvider, useMessagePageContext };
